import type { LicenseFeatureGroupChange } from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupComposeFooter"
import {
  isCategoryRule,
  isModuleRule,
  ruleKey,
  type FeatureGroupPickedLike,
  type FeatureGroupRuleLike,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import type { FeatureGroupRuleOptionsQuery } from "@/v2-backoffice-client/license/featureGroup/__types__/license.featureGroup.ruleOptions.query"

type RuleOption =
  FeatureGroupRuleOptionsQuery["getFeatureGroupRuleOptions"][number]

export function toPickedMap(features: readonly FeatureGroupPickedLike[]) {
  return new Map(features.map((feature) => [feature.Code, feature]))
}

function labelOf(feature: FeatureGroupPickedLike) {
  return feature.Name ?? feature.Code
}

/** The pending edits to the picked set, as the footer lists them. */
export function pickedChanges(
  persisted: ReadonlyMap<string, FeatureGroupPickedLike>,
  draft: ReadonlyMap<string, FeatureGroupPickedLike>,
): LicenseFeatureGroupChange[] {
  const changes: LicenseFeatureGroupChange[] = []
  for (const [code, feature] of draft) {
    if (!persisted.has(code)) {
      changes.push({
        kind: "added",
        label: labelOf(feature),
        detail: feature.ModuleName ?? feature.FeatureTypeName ?? undefined,
      })
    }
  }
  for (const [code, feature] of persisted) {
    if (!draft.has(code)) {
      changes.push({
        kind: "removed",
        label: labelOf(feature),
        detail: feature.ModuleName ?? feature.FeatureTypeName ?? undefined,
      })
    }
  }
  return changes
}

/**
 * The pending edits to the rules, as the footer lists them: a content class
 * or a console module granted or revoked as a whole ("Everything in Map
 * layers", "Everything in Property"), a layer category granted or revoked.
 */
export function ruleChanges(
  options: readonly RuleOption[],
  persisted: readonly FeatureGroupRuleLike[],
  draft: readonly FeatureGroupRuleLike[],
): LicenseFeatureGroupChange[] {
  const classLabel = new Map(
    options
      .filter((option) => option.FeatureTypeCode != null)
      .map((option) => [option.FeatureTypeCode ?? -1, option.Label]),
  )
  const categoryLabel = new Map(
    options
      .filter(isCategoryRule)
      .map((option) => [option.LayerCategoryFeatureCode ?? "", option.Label]),
  )
  const moduleLabel = new Map(
    options
      .filter((option) => isModuleRule(option))
      .map((option) => [option.ModuleCode ?? "", option.Label]),
  )
  function describe(rule: FeatureGroupRuleLike) {
    if (isCategoryRule(rule)) {
      const code = rule.LayerCategoryFeatureCode ?? ""
      return {
        label: categoryLabel.get(code) ?? code,
        detail: "layer category",
      }
    }
    if (isModuleRule(rule)) {
      const code = rule.ModuleCode ?? ""
      return {
        label: `Everything in ${moduleLabel.get(code) ?? code}`,
        detail: "now and later",
      }
    }
    const typeCode = rule.FeatureTypeCode ?? -1
    return {
      label: `Everything in ${classLabel.get(typeCode) ?? `type ${typeCode}`}`,
      detail: "now and later",
    }
  }
  const persistedKeys = new Set(persisted.map(ruleKey))
  const draftKeys = new Set(draft.map(ruleKey))
  return [
    ...draft
      .filter((rule) => !persistedKeys.has(ruleKey(rule)))
      .map((rule) => ({ kind: "added" as const, ...describe(rule) })),
    ...persisted
      .filter((rule) => !draftKeys.has(ruleKey(rule)))
      .map((rule) => ({ kind: "removed" as const, ...describe(rule) })),
  ]
}
